import { Box, Container, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from 'react-i18next';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { offerService } from '../services/offerService';
import { useSnackbar } from '@/shared/components/notifications';
import { LocationSection } from './LocationSection';
import { ProductSection } from './ProductSection';
import { AppButton } from '@/shared/components';
import { AvailabilitySection } from './AvailabilitySection';
import { DocumentSection } from './DocumentSection';
import { TitleSection } from './TitleSection';
import { DescriptionSection } from './DescriptionSection';
import { OfferFormData, ProductFormData } from '../types/offer.types';
import { usePersistentForm } from '../hooks/usePersistentForm';

function getDefaultEndDate(): Date {
  const date = new Date();
  date.setFullYear(date.getFullYear() + 1);
  return date;
}

export function PublishPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showSuccess, showError } = useSnackbar();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const maxProducts = Number(import.meta.env.VITE_MAX_PRODUCTS_PER_OFFER) || 10;

  const defaultProduct: ProductFormData = {
    materialId: '',
    materialCategory: '',
    materialSubcategory: '',
    unitPrice: '',
    quantity: '',
    unitId: '',
    condition: 'used',
    photos: [],
  };

  const methods = useForm<OfferFormData>({
    defaultValues: {
      title: '',
      description: '',
      address: '',
      products: [defaultProduct],
      startDate: new Date(),
      endDate: getDefaultEndDate(),
      documents: [],
    },
    mode: 'onChange', // Validate on change for real-time button state
  });

  const { fields, append, remove } = useFieldArray({
    control: methods.control,
    name: 'products',
  });

  // Enable form draft persistence (auto-save to localStorage)
  const { clearDraft } = usePersistentForm(methods, { restoreOnMount: false });

  const onSubmit = async (data: OfferFormData) => {
    try {
      setIsSubmitting(true);

      console.log('Form data before transform:', data);

      // Serialize a Date using local calendar date (YYYY-MM-DD) at UTC midnight,
      // avoiding timezone shift when converting local noon/midnight to UTC ISO string.
      const toLocalDateISO = (date: Date): string => {
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const d = String(date.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}T00:00:00.000Z`;
      };

      // Transform frontend form data to backend API format
      const payload = {
        title: data.title,
        description: data.description,
        address: data.address,
        latitude: data.latitude,
        longitude: data.longitude,
        startDate: toLocalDateISO(data.startDate ?? new Date()),
        endDate: toLocalDateISO(data.endDate ?? new Date()),
        products: data.products.map(product => ({
          materialId: product.materialId,
          materialCategory: product.materialCategory,
          materialSubcategory: product.materialSubcategory,
          unitPrice: product.unitPrice ? Number.parseFloat(product.unitPrice) : 0,
          quantity: Number.parseFloat(product.quantity),
          unitId: product.unitId,
          status: product.condition === 'new' ? 'NEW' : ('USED' as 'NEW' | 'USED'),
        })),
      };

      const photoCounts = data.products.map(product => product.photos?.length ?? 0);
      const photos = data.products.flatMap(product => product.photos || []);

      console.log('Payload to send:', payload);

      // Call API with photos
      await offerService.createOffer(payload, photos, data.documents || [], photoCounts);

      // Show success notification
      showSuccess(t('publish.successMessage'));

      // Clear saved draft from localStorage and IndexedDB
      await clearDraft();

      // Reset form after successful submission (including all fields)
      methods.reset({
        title: '',
        description: '',
        address: '',
        latitude: undefined,
        longitude: undefined,
        products: [defaultProduct],
        startDate: new Date(),
        endDate: getDefaultEndDate(),
        documents: [],
      });

      // Navigate to my publications page
      navigate('/my-publications');
    } catch (error) {
      console.error('Error creating offer:', error);

      // Map backend validation errors to form fields
      if (error && typeof error === 'object' && 'response' in error) {
        const response = (error as { response?: { data?: { message?: string | string[] } } })
          .response;
        const messages = response?.data?.message;

        if (Array.isArray(messages)) {
          // Map backend field errors to form fields
          messages.forEach((msg: string) => {
            const productMatch =
              msg.match(/products\[(\d+)\]\.(\w+)/) || msg.match(/products\.(\d+)\.(\w+)/);
            if (productMatch) {
              const index = Number.parseInt(productMatch[1], 10);
              const field = productMatch[2];
              const fieldPath = `products.${index}.${field}`;
              methods.setError(fieldPath as Parameters<typeof methods.setError>[0], {
                type: 'manual',
                message: msg,
              });
              return;
            }
            if (msg.includes('address')) {
              methods.setError('address', { type: 'manual', message: msg });
            } else if (msg.includes('description')) {
              methods.setError('description', { type: 'manual', message: msg });
            } else if (msg.includes('product') || msg.includes('material')) {
              methods.setError('products.0.materialId', { type: 'manual', message: msg });
            } else if (msg.includes('quantity')) {
              methods.setError('products.0.quantity', { type: 'manual', message: msg });
            } else if (
              msg.toLowerCase().includes('unit price') ||
              msg.toLowerCase().includes('unitprice')
            ) {
              methods.setError('products.0.unitPrice', { type: 'manual', message: msg });
            } else if (msg.includes('unit')) {
              methods.setError('products.0.unitId', { type: 'manual', message: msg });
            } else if (msg.includes('startDate') || msg.includes('start date')) {
              methods.setError('startDate', { type: 'manual', message: msg });
            } else if (msg.includes('endDate') || msg.includes('end date')) {
              methods.setError('endDate', { type: 'manual', message: msg });
            }
          });
        }
      }

      // Show error notification
      const errorMessage = error instanceof Error ? error.message : t('publish.errorMessage');
      showError(errorMessage);

      // Scroll to first error field
      const firstErrorField = Object.keys(methods.formState.errors)[0];
      if (firstErrorField) {
        methods.setFocus(firstErrorField as unknown as Parameters<typeof methods.setFocus>[0]);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const { isValid } = methods.formState;

  return (
    <Box sx={{ pb: 10, pt: 0 }}>
      <Container maxWidth="md" sx={{ py: 3 }}>
        <FormProvider {...methods}>
          <Box component="form" onSubmit={methods.handleSubmit(onSubmit)}>
            <TitleSection />

            <LocationSection />

            {fields.map((field, index) => (
              <ProductSection
                key={field.id}
                index={index}
                canRemove={fields.length > 1}
                onRemove={() => remove(index)}
              />
            ))}

            <AppButton
              type="button"
              variant="outlined"
              fullWidth
              onClick={() => append({ ...defaultProduct })}
              disabled={fields.length >= maxProducts}
              data-testid="publish-product-add"
              startIcon={<AddIcon />}
              sx={{
                mt: 1,
                mb: fields.length >= maxProducts ? 1 : 3,
                py: 1.25,
                borderRadius: 1,
                borderWidth: 2,
                borderColor: 'primary.main',
                color: 'primary.main',
                fontWeight: 600,
                textTransform: 'none',
                '&:hover': {
                  borderWidth: 2,
                  backgroundColor: 'primary.main',
                  color: 'primary.contrastText',
                },
              }}
            >
              {t('publish.product.addProduct')}
            </AppButton>

            {fields.length >= maxProducts && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 3, textAlign: 'center' }}
                data-testid="publish-product-max-reached"
              >
                {t('publish.product.maxProductsReached', { max: maxProducts })}
              </Typography>
            )}

            <DescriptionSection />

            <AvailabilitySection />

            <DocumentSection />

            <AppButton
              type="submit"
              variant="primary"
              fullWidth
              disabled={!isValid}
              loading={isSubmitting}
            >
              {isSubmitting ? t('publish.submitting') : t('publish.submitButton')}
            </AppButton>
          </Box>
        </FormProvider>
      </Container>
    </Box>
  );
}
